import React, { useState, useEffect } from 'react'
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { useDispatch, useSelector } from 'react-redux'
import { dispatchHandler } from '../../../../helper/apiHelperFunctions'
import { formatNumber } from '../../../../helper/genericFunction'
import LanguageTranslator from '../../../../locals/LanguageTranslator'

const LegendDot = ({ color, label }) => (
    <div className='flex items-center gap-[8px]'>
        <span className='size-[8px] rounded-full shrink-0' style={{ backgroundColor: color, boxShadow: `0 0 0 2px ${color}33` }} />
        <LanguageTranslator tag='p' className='mb-0 capitalize text-[11px] font-medium text-[#8D99AC] whitespace-nowrap'>{label}</LanguageTranslator>
    </div>
)

const PoGrcTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;

    const data = payload[0].payload;
    const poQty = formatNumber(data.po || 0).short;
    const poAmt = formatNumber(data.poValueForTooltip || 0).short;
    const grcQty = formatNumber(data.grc || 0).short;
    const grcAmt = formatNumber(data.grcValueForTooltip || 0).short;
    
    // safe fulfillment calculation
    const fulfillment = (data.po + data.grc) === 0 ? 0 : Math.round((data.grc / data.po) * 100) || 0;

    return (
        <div className='bg-white border border-[#EBEBEB] rounded-[10px] shadow-[0px_0px_4.395px_0px_rgba(0,0,0,0.15)] w-[213px] p-[12px]'>
            <p className='mb-0 text-[12px] font-semibold text-black'>{label}</p>

            <div className='mt-[8px] pt-[8px] border-t border-borderSlateGrey flex items-center'>
                <div className='w-[57px]' />
                <LanguageTranslator tag='p' className='mb-0 flex-1 text-[11px] font-medium text-[#8D99AC]'>Qty</LanguageTranslator>
                <LanguageTranslator tag='p' className='mb-0 flex-1 text-[11px] font-medium text-[#8D99AC]'>Amount</LanguageTranslator>
            </div>

            <div className='mt-[6px] flex items-center'>
                <div className='w-[57px]'><LegendDot color='#5D5FEF' label='PO' /></div>
                <p className='mb-0 flex-1 text-[12px] font-semibold text-black'>{poQty}</p>
                <p className='mb-0 flex-1 text-[12px] font-semibold text-black'>₹{poAmt}</p>
            </div>

            <div className='mt-[6px] flex items-center'>
                <div className='w-[57px]'><LegendDot color='#00B894' label='GRC' /></div>
                <div className='flex-1 flex items-center gap-[6px]'>
                    <p className='mb-0 text-[12px] font-semibold text-black'>{grcQty}</p>
                    <p className='mb-0 text-[11px] font-medium text-[#03B675]'>{fulfillment}%</p>
                </div>
                <p className='mb-0 flex-1 text-[12px] font-semibold text-black'>₹{grcAmt}</p>
            </div>
        </div>
    )
}

// The two tinted blocks sitting to the right of the chart.
const PoGrcMetric = ({ item }) => {
    return (
        <div className='rounded-[10px] p-[12px] flex flex-col justify-between gap-[12px]' style={{ backgroundColor: item?.background }}>
            <div className='flex items-start justify-between gap-3'>
                <LanguageTranslator tag='p' className='mb-0 text-[11px] font-normal text-black'>{item?.label}</LanguageTranslator>
                <div className='flex flex-col items-end text-right'>
                    <LanguageTranslator tag='p' className='mb-0 text-[11px] font-normal text-[#4A5D7A] whitespace-nowrap'>{item?.rightLabel}</LanguageTranslator>
                    <p className='mb-0 text-[14px] font-semibold text-[#111D31] whitespace-nowrap'>{item?.rightValue}</p>
                </div>
            </div>

            <div className='flex items-end justify-between gap-3'>
                <div className='w-[90px] shrink-0 flex flex-col gap-[4px]'>
                    <p className='mb-0 text-[20px] font-semibold text-black leading-none'>{item?.value}</p>
                    <div className='h-[6px] w-full rounded-[20px] bg-[#D9D9D9] overflow-hidden'>
                        <div className='h-full rounded-[20px]' style={{ width: `${item?.percentage}%`, backgroundColor: item?.barColor }} />
                    </div>
                </div>
                <div className='flex flex-col items-end text-right'>
                    <LanguageTranslator tag='p' className='mb-0 text-[11px] font-normal text-[#4A5D7A] whitespace-nowrap'>{item?.bottomLabel}</LanguageTranslator>
                    <p className='mb-0 text-[14px] font-semibold text-[#111D31] whitespace-nowrap'>{item?.bottomValue}</p>
                </div>
            </div>
        </div>
    )
}

const PoAmountQtyCard = () => {
    const dispatch = useDispatch();
    const getPoGrcMonthlySummaryData = useSelector(state => state?.digivendStore?.getPoGrcMonthlySummaryData || {});
    const getPoGrcKpiSummaryData = useSelector(state => state?.digivendStore?.getPoGrcKpiSummaryData || {});

    const [trendData, setTrendData] = useState([]);
    const [metricsData, setMetricsData] = useState([]);

    useEffect(() => {
        dispatchHandler(dispatch, "getPoGrcMonthlySummaryRequest", { displayName: "PO_GRC_MONTHLY_SUMMARY" });
        dispatchHandler(dispatch, "getPoGrcKpiSummaryRequest", { displayName: "PO_GRC_KPI_SUMMARY" });
    }, [dispatch]);

    useEffect(() => {
        if (getPoGrcMonthlySummaryData?.isSuccess) {
            const responseData = getPoGrcMonthlySummaryData?.data?.data?.resource || getPoGrcMonthlySummaryData?.data?.resource;
            if (responseData && Array.isArray(responseData)) {
                const mappedTrend = responseData.map(item => {
                    const d = new Date(item.month);
                    const monthStr = d.toLocaleString('en-US', { month: 'short' });
                    const yearStr = d.getFullYear().toString().slice(-2);

                    return {
                        ...item,
                        month: `${monthStr} '${yearStr}`,
                        po: item.order_qty || 0,
                        grc: item.grc_qty || 0,
                        poValueForTooltip: item.po_amount || 0,
                        grcValueForTooltip: item.grc_amt || 0
                    };
                });
                setTrendData(mappedTrend);
            }
        }
    }, [getPoGrcMonthlySummaryData?.isSuccess, getPoGrcMonthlySummaryData?.data]);

    useEffect(() => {
        if (getPoGrcKpiSummaryData?.isSuccess) {
            const res = getPoGrcKpiSummaryData?.data?.data?.resource || getPoGrcKpiSummaryData?.data?.resource;
            if (res) {
                const poToGrcPct = typeof res.po_to_grc_pct !== "undefined" ? Math.round(Number(res.po_to_grc_pct)) : 0;
                const fulfillmentPct = typeof res.fulfillment_rate !== "undefined" ? Math.round(Number(res.fulfillment_rate)) : 0;
                
                const poAmt = formatNumber(res.total_po_amount || 0).short;
                const grcAmt = formatNumber(res.total_grc_amount || 0).short;
                const pendingAmt = formatNumber(res.pending_amount || 0).short;

                const dynamicMetrics = [
                    { 
                        id: "poToGrc", 
                        label: "PO to GRC%", 
                        value: `${poToGrcPct}%`, 
                        percentage: poToGrcPct, 
                        barColor: "#FE9D0B", 
                        background: "rgba(254, 157, 11, 0.05)", 
                        rightLabel: "Total PO Value", 
                        rightValue: `₹${poAmt}`, 
                        bottomLabel: "Total GRC Value", 
                        bottomValue: `₹${grcAmt}` 
                    },
                    { 
                        id: "fulfillment", 
                        label: "Fulfillment rate", 
                        value: `${fulfillmentPct}%`, 
                        percentage: fulfillmentPct, 
                        barColor: "#03B675", 
                        background: "rgba(3, 182, 117, 0.06)", 
                        rightLabel: "Pending Value", 
                        rightValue: `₹${pendingAmt}`, 
                        bottomLabel: "Pending Amount", 
                        bottomValue: `₹${pendingAmt}` 
                    },
                ];
                setMetricsData(dynamicMetrics);
            }
        }
    }, [getPoGrcKpiSummaryData?.isSuccess, getPoGrcKpiSummaryData?.data]);

    const isLoading = getPoGrcMonthlySummaryData?.isLoading || getPoGrcKpiSummaryData?.isLoading;

    return (
        <div className={`bg-white border border-borderSlateGrey rounded-[10px] p-[12px] ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}>
            <div className='flex items-center justify-between gap-3'>
                <LanguageTranslator tag='p' className='mb-0 text-[16px] font-semibold text-fontBlack'>PO to GRC Qty</LanguageTranslator>
                <div className='flex items-center gap-[10px]'>
                    <LegendDot color='#5D5FEF' label='Purchase Orders' />
                    <LegendDot color='#00B894' label='GRC' />
                </div>
            </div>

            <div className='mt-[8px] flex flex-col xl:flex-row gap-[12px]'>
                <div className='flex-1 min-w-0 h-[255px]'>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={trendData} margin={{ top: 10, right: 8, left: 0, bottom: 0 }} barCategoryGap="35%">
                            <XAxis
                                dataKey="month"
                                tickLine={false}
                                axisLine={false}
                                interval={0}
                                tick={{ fill: "#4A5D7A", fontSize: 11 }}
                            />
                            <YAxis
                                tickLine={false}
                                axisLine={false}
                                width={38}
                                domain={['auto', 'auto']}
                                tick={{ fill: "#4A5D7A", fontSize: 11 }}
                                tickFormatter={(val) => formatNumber(val).short}
                            />
                            <Tooltip content={<PoGrcTooltip />} cursor={{ fill: "rgba(93, 95, 239, 0.06)" }} />
                            <Bar dataKey="po" stackId="poGrc" fill="#5D5FEF" fillOpacity={0.8} radius={[2, 2, 2, 2]} maxBarSize={18} isAnimationActive={false} />
                            <Bar dataKey="grc" stackId="poGrc" fill="#03B675" fillOpacity={0.8} radius={[6, 6, 0, 0]} maxBarSize={18} isAnimationActive={false} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                <div className='w-full xl:w-[266px] shrink-0 flex flex-col gap-[11px]'>
                    {metricsData?.map(item => <PoGrcMetric key={item?.id} item={item} />)}
                </div>
            </div>
        </div>
    )
}

export default PoAmountQtyCard
